import React, { useState, useEffect } from 'react';
import { getData } from '@/app/funcs/api';
import './CategoriesFilter.css';

const CategoriesFilter = ({ onCategorySelect, selectedCategory = null, postType = 'posts', allData = [], totalPosts = 0 }) => {
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCategories = async () => {
            try {
                setLoading(true);

                // Fetch ALL posts of the specific type to get accurate category counts
                // We'll use a larger per_page to get more posts at once
                const allPosts = await getData(`${postType}?per_page=100&_embed`);

                // Note: If we fetched fewer posts than total, category counts might be incomplete
                // if (allPosts.length < totalPosts) {
                // }


                // Extract unique categories from ALL posts
                const categoryMap = new Map();
                allPosts.forEach(post => {
                    if (post.categories && Array.isArray(post.categories)) {
                        post.categories.forEach(categoryId => {
                            if (!categoryMap.has(categoryId)) {
                                // Create a basic category object
                                categoryMap.set(categoryId, {
                                    id: categoryId,
                                    count: 1,
                                    name: `Category ${categoryId}` // Will be updated if we can fetch category details
                                });
                            } else {
                                categoryMap.get(categoryId).count++;
                            }
                        });
                    }
                });


                // Try to fetch category details for the IDs we found
                const categoryIds = Array.from(categoryMap.keys());
                if (categoryIds.length > 0) {
                    try {

                        // Try multiple approaches to get category details
                        let categoryDetails = [];

                        // First try: include parameter
                        try {
                            const includeResponse = await getData(`categories?include=${categoryIds.join(',')}`);

                            // Check if response is an array or has a different structure
                            if (Array.isArray(includeResponse)) {
                                categoryDetails = includeResponse;
                            } else if (includeResponse.data && Array.isArray(includeResponse.data)) {
                                categoryDetails = includeResponse.data;
                            }
                            // else {
                            // }

                        } catch (includeError) {

                            // Second try: fetch categories individually
                            try {
                                const individualPromises = categoryIds.map(async (id) => {
                                    try {
                                        const cat = await getData(`categories/${id}`);
                                        return cat;
                                    } catch (error) {
                                        return null;
                                    }
                                });

                                const individualResults = await Promise.all(individualPromises);
                                categoryDetails = individualResults.filter(cat => cat !== null);
                            } catch (individualError) {
                                throw individualError;
                            }
                        }

                        // Update our category map with real names and slugs
                        if (categoryDetails.length > 0) {
                            categoryDetails.forEach(cat => {
                                if (categoryMap.has(cat.id)) {
                                    const existing = categoryMap.get(cat.id);
                                    const originalName = existing.name;

                                    // Try different possible name fields
                                    const newName = cat.name || cat.title || cat.rendered?.title || `Category ${cat.id}`;
                                    const newSlug = cat.slug || cat.name?.toLowerCase().replace(/\s+/g, '-') || `category-${cat.id}`;

                                    existing.name = newName;
                                    existing.slug = newSlug;

                                }
                            });
                        } else {

                            // Fallback: try to extract category names from embedded data in posts
                            try {
                                allPosts.forEach(post => {
                                    if (post._embedded && post._embedded['wp:term']) {
                                        const terms = post._embedded['wp:term'];
                                        terms.forEach(termGroup => {
                                            if (Array.isArray(termGroup)) {
                                                termGroup.forEach(term => {
                                                    if (term.taxonomy === 'category' && categoryMap.has(term.id)) {
                                                        const existing = categoryMap.get(term.id);
                                                        const originalName = existing.name;
                                                        existing.name = term.name || `Category ${term.id}`;
                                                        existing.slug = term.slug || `category-${term.id}`;
                                                    }
                                                });
                                            }
                                        });
                                    }
                                });
                            } catch (embeddedError) {
                            }
                        }

                    } catch (catError) {
                    }
                }

                let categoriesData = Array.from(categoryMap.values());


                // Filter out categories with "bloge" or "blog" in name or slug (case-insensitive)
                const filteredCategories = categoriesData.filter(category => {
                    const name = category.name?.toLowerCase() || '';
                    const slug = category.slug?.toLowerCase() || '';

                    // Exclude categories containing "bloge" or "blog"
                    const isBlogCategory = name.includes('blog') || slug.includes('blog') ||
                        name.includes('bloge') || slug.includes('bloge');

                    return !isBlogCategory;
                });

                setCategories(filteredCategories);

            } catch (error) {
                setCategories([]);
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, [postType, totalPosts]);

    const handleCategoryClick = (category) => {
        if (selectedCategory && selectedCategory.id === category.id) {
            // If clicking the same category, deselect it
            onCategorySelect(null);
        } else {
            // Select the new category
            onCategorySelect(category);
        }
    };

    if (loading) {
        return (
            <div className="categories-filter-container bg-light border-1">
                <div className="d-flex justify-content-center">
                    <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">جاري التحميل...</span>
                    </div>
                </div>
            </div>
        );
    }

    // Don't show the filter if no categories are available
    if (categories.length === 0) {
        return (
            <div className="categories-filter-container bg-light border-1 d-none">
                <div className="text-center text-muted">
                    <small>لا توجد فئات متاحة لهذا النوع من المحتوى</small>
                </div>
            </div>
        );
    }

    return (
        <div className="categories-filter-container  bg-light border-1">
            <div className="categories-badges">
                {/* All Categories Option */}
                <button
                    className={`category-badge btn btn-outline-dark ${!selectedCategory ? 'active' : ''}`}
                    onClick={() => onCategorySelect(null)}
                >
                    الكل
                    <span className="category-count">({totalPosts || allData.length || 0})</span>
                </button>

                {/* Individual Category Options */}
                {categories.map((category) => (
                    <button
                        key={category.id}
                        className={`category-badge btn btn-outline-dark ${selectedCategory && selectedCategory.id === category.id ? 'active' : ''}`}
                        onClick={() => handleCategoryClick(category)}
                    >
                        {category.name}
                        <span className="category-count">({category.count})</span>
                    </button>
                ))}
            </div>
        </div>
    );
};

// Display-only variant for simpler use cases
export const CategoriesDisplay = ({ categories = [], postType = 'posts' }) => {
    const filteredCategories = categories.filter(category => {
        const name = category.name?.toLowerCase() || '';
        const slug = category.slug?.toLowerCase() || '';

        // Exclude categories containing "bloge" or "blog"
        const isBlogCategory = name.includes('blog') || slug.includes('blog') ||
            name.includes('bloge') || slug.includes('bloge');

        return !isBlogCategory;
    });

    if (filteredCategories.length === 0) {
        return null;
    }

    return (
        <div className="categories-filter-container  bg-light border-1">
            <div className="categories-badges">
                {filteredCategories.map((category) => (
                    <span key={category.id} className="category-badge  btn btn-outline-dark display-only">
                        {category.name}
                        <span className="category-count">({category.count})</span>
                    </span>
                ))}
            </div>
        </div>
    );
};

export default CategoriesFilter;
